import { useEffect, useMemo, useRef } from "react";
import { SolanaLogo } from "./SolanaLogo";

const rand = (min, max) => Math.random() * (max - min) + min;

const Starfield = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let stars = [];
    // Mobile: fewer stars + pause the rAF loop while the page is actively
    // scrolling (main-thread contention is what causes scroll jank).
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const STAR_COUNT = isMobile ? 60 : 140;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      stars = Array.from({ length: STAR_COUNT }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: rand(0.4, 1.8),
        phase: rand(0, Math.PI * 2),
        speed: rand(0.4, 1.6),
        hue: Math.random() > 0.85 ? "#9945FF" : Math.random() > 0.7 ? "#14F195" : "#ffffff",
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = (t) => {
      // Skip painting while the user is actively scrolling on mobile — the
      // last painted frame stays visible; scroll perf is what people notice.
      if (isMobile && document.body.classList.contains("is-scrolling")) {
        raf = requestAnimationFrame(draw);
        return;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of stars) {
        const a = 0.25 + 0.75 * Math.abs(Math.sin(s.phase + (t / 1000) * s.speed));
        ctx.globalAlpha = a;
        ctx.fillStyle = s.hue;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
        if (s.r > 1.3) {
          ctx.globalAlpha = a * 0.5;
          ctx.fillRect(s.x - s.r * 3, s.y - 0.4, s.r * 6, 0.8);
          ctx.fillRect(s.x - 0.4, s.y - s.r * 3, 0.8, s.r * 6);
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" />;
};

export const SpaceBackground = () => {
  const logos = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        id: i,
        left: `${rand(2, 95)}%`,
        size: rand(18, 52),
        dur: `${rand(14, 32)}s`,
        delay: `${rand(-30, 0)}s`,
        op: rand(0.25, 0.7).toFixed(2),
        rotA: `${rand(-40, 40)}deg`,
        rotB: `${rand(-140, 140)}deg`,
        sc: rand(0.7, 1.2).toFixed(2),
      })),
    []
  );

  return (
    <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          // AVIF → WebP → PNG fallback via image-set (visually identical, up to 30× smaller)
          backgroundImage:
            "image-set(url(/assets/space-bg.avif) type('image/avif'), url(/assets/space-bg.webp) type('image/webp'), url(/assets/space-bg.png) type('image/png'))",
        }}
      />
      <div className="absolute inset-0 bg-[#050409]/75" />
      <Starfield />
      {logos.map((l) => (
        <span
          key={l.id}
          className="falling-sol"
          style={{
            left: l.left,
            "--dur": l.dur,
            "--delay": l.delay,
            "--op": l.op,
            "--rot-a": l.rotA,
            "--rot-b": l.rotB,
            "--sc": l.sc,
          }}
        >
          <SolanaLogo size={l.size} />
        </span>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050409]" />
    </div>
  );
};
